import React, { useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Search, Tag, Star, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { useConfirm } from "../components/ConfirmDialog";
import { api } from "../lib/api";
import { toast } from "sonner";

const ALLERGENS_EU = [
  "Gluten", "Crustacés", "Œufs", "Poissons", "Arachides", "Soja", "Lait",
  "Fruits à coque", "Céleri", "Moutarde", "Sésame", "Sulfites", "Lupin", "Mollusques",
];

const PRICE_UNITS = ["kg", "g", "L", "mL", "pce"];
const REF_UNITS = ["g", "kg", "mL", "L", "pce"];

const emptyForm = {
  name: "",
  category: "Divers",
  unit: "g",
  default_price_chf: "",
  default_price_unit: "kg",
  pack_price_chf: "",
  pack_size: "",
  pack_size_unit: "g",
  default_supplier_id: "",
  supplier_prices: [],
  allergens: [],
  notes: "",
  yield_percent: "",
};

const emptySupplierPrice = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
  supplier_id: "",
  price_chf: "",
  price_unit: "kg",
  pack_price_chf: "",
  pack_size: "",
  pack_size_unit: "g",
  is_preferred: false,
  notes: "",
  _packOpen: false, // UI uniquement, retiré au submit
});

// Convertit une quantité vers son équivalent en unité de référence (kg ou L)
// pour normaliser le calcul du prix CHF/<unit>
const TO_REF = {
  g: { factor: 0.001, ref: "kg" },
  kg: { factor: 1, ref: "kg" },
  mL: { factor: 0.001, ref: "L" },
  L: { factor: 1, ref: "L" },
  pce: { factor: 1, ref: "pce" },
};

function computePackUnitPrice(packPrice, packSize, packUnit) {
  const p = Number(packPrice);
  const s = Number(packSize);
  if (!p || !s || s <= 0 || !packUnit) return null;
  const conv = TO_REF[packUnit];
  if (!conv) return null;
  // Prix CHF par 1 unité de référence (kg, L ou pce)
  const refAmount = s * conv.factor;
  if (refAmount <= 0) return null;
  return { price: p / refAmount, unit: conv.ref };
}

export default function Ingredients() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]); // Sert seulement à mémoriser le mapping {id: count}
  const [usageCount, setUsageCountState] = useState({}); // {ingredient_id: count}
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("__all__");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = créer, sinon = id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    // Phase 32 perf — on ne charge plus toute la collection /products (recipes complètes
    // = 28+ KB inutiles). On utilise l'endpoint d'agrégation léger /ingredients-usage.
    const [a, b, c, d] = await Promise.all([
      api.get("/ingredients"),
      api.get("/ingredient-categories"),
      api.get("/suppliers"),
      api.get("/ingredients-usage"),
    ]);
    setItems(a.data);
    setCategories(b.data);
    setSuppliers(c.data);
    setUsageCountState(d.data || {});
    setProducts([]); // legacy state — gardé vide pour ne rien casser
  };
  useEffect(() => { load(); }, []);

  // Phase 32 perf — `usageCount` est désormais alimenté par l'endpoint léger
  // /api/ingredients-usage (cf. setUsageCountState dans load()). L'ancien memo
  // qui itérait sur toute la collection products côté front a été supprimé.

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filterCat !== "__all__" && it.category !== filterCat) return false;
      if (!q) return true;
      return (it.name || "").toLowerCase().includes(q)
        || (it.notes || "").toLowerCase().includes(q);
    });
  }, [items, search, filterCat]);

  const supplierMap = useMemo(() => Object.fromEntries(suppliers.map((s) => [s.id, s])), [suppliers]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it.id);
    // Convertit les supplier_prices serveur -> form (avec _packOpen=false)
    const sp = (it.supplier_prices && it.supplier_prices.length > 0)
      ? it.supplier_prices.map((p) => ({
          id: p.id || `tmp-${Math.random().toString(36).slice(2, 10)}`,
          supplier_id: p.supplier_id || "",
          price_chf: p.price_chf ?? "",
          price_unit: p.price_unit || "kg",
          pack_price_chf: p.pack_price_chf ?? "",
          pack_size: p.pack_size ?? "",
          pack_size_unit: p.pack_size_unit || "g",
          is_preferred: !!p.is_preferred,
          notes: p.notes || "",
          _packOpen: false,
        }))
      : [];
    // Fallback : si pas de supplier_prices mais default_* existant, on seed 1 ligne
    if (sp.length === 0 && (it.default_price_chf != null || it.default_supplier_id)) {
      sp.push({
        ...emptySupplierPrice(),
        supplier_id: it.default_supplier_id || "",
        price_chf: it.default_price_chf ?? "",
        price_unit: it.default_price_unit || "kg",
        pack_price_chf: it.pack_price_chf ?? "",
        pack_size: it.pack_size ?? "",
        pack_size_unit: it.pack_size_unit || "g",
        is_preferred: true,
      });
    }
    setForm({
      name: it.name || "",
      category: it.category || "Divers",
      unit: it.unit || "g",
      default_price_chf: it.default_price_chf ?? "",
      default_price_unit: it.default_price_unit || "kg",
      pack_price_chf: it.pack_price_chf ?? "",
      pack_size: it.pack_size ?? "",
      pack_size_unit: it.pack_size_unit || "g",
      default_supplier_id: it.default_supplier_id || "",
      supplier_prices: sp,
      allergens: it.allergens || [],
      notes: it.notes || "",
      yield_percent: it.yield_percent ?? "",
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Nom obligatoire"); return; }
    setSaving(true);
    // Sérialise supplier_prices : convertit en nombres, retire les champs UI internes,
    // garantit qu'au moins une ligne est marquée préférée si la liste est non vide.
    const cleanSP = (form.supplier_prices || [])
      .filter((p) => p.price_chf !== "" || p.supplier_id || p.pack_price_chf !== "")
      .map((p) => ({
        id: p.id && !p.id.startsWith("tmp-") ? p.id : undefined,
        supplier_id: p.supplier_id || null,
        price_chf: p.price_chf === "" ? null : Number(p.price_chf),
        price_unit: p.price_unit || "kg",
        pack_price_chf: p.pack_price_chf === "" ? null : Number(p.pack_price_chf),
        pack_size: p.pack_size === "" ? null : Number(p.pack_size),
        pack_size_unit: p.pack_size_unit || null,
        is_preferred: !!p.is_preferred,
        notes: p.notes || "",
      }));
    if (cleanSP.length > 0 && !cleanSP.some((p) => p.is_preferred)) {
      cleanSP[0].is_preferred = true;
    }
    // Retire les `id` non définis pour laisser le backend en générer
    const sanitizedSP = cleanSP.map((p) => {
      const out = { ...p };
      if (!out.id) delete out.id;
      return out;
    });
    const payload = {
      name: form.name.trim(),
      category: form.category,
      unit: form.unit,
      allergens: form.allergens,
      notes: form.notes,
      yield_percent: form.yield_percent === "" ? null : Number(form.yield_percent),
      supplier_prices: sanitizedSP,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/ingredients/${editing}`, payload);
        toast.success("Ingrédient mis à jour");
        setItems((prev) => prev.map((it) => (it.id === editing ? data : it)));
      } else {
        const { data } = await api.post("/ingredients", payload);
        toast.success(`Ingrédient « ${data.name} » créé`);
        setItems((prev) => {
          const next = [...prev, data];
          next.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"));
          return next;
        });
      }
      setOpen(false);
      load().catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    const ok = await confirm({
      title: `Supprimer « ${it.name} » ?`,
      description: "Les recettes existantes qui le référencent ne sont pas modifiées (le nom y est figé). Action irréversible.",
      confirmText: "Supprimer",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await api.delete(`/ingredients/${it.id}`);
      toast.success("Ingrédient supprimé");
      // Update optimiste
      setItems((prev) => prev.filter((x) => x.id !== it.id));
      load().catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const toggleAllergen = (a) => {
    setForm((f) => ({
      ...f,
      allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a],
    }));
  };

  // ============== Helpers gestion supplier_prices ==============
  const addSupplierPrice = () => {
    setForm((f) => {
      const newSP = emptySupplierPrice();
      // Si c'est la 1re ligne, on la marque préférée par défaut
      if ((f.supplier_prices || []).length === 0) newSP.is_preferred = true;
      return { ...f, supplier_prices: [...(f.supplier_prices || []), newSP] };
    });
  };
  const updateSupplierPrice = (idx, patch) => {
    setForm((f) => ({
      ...f,
      supplier_prices: f.supplier_prices.map((p, i) => i === idx ? { ...p, ...patch } : p),
    }));
  };
  const removeSupplierPrice = (idx) => {
    setForm((f) => {
      const next = f.supplier_prices.filter((_, i) => i !== idx);
      // Si on supprime la préférée, la 1re restante devient préférée
      if (next.length > 0 && !next.some((p) => p.is_preferred)) next[0].is_preferred = true;
      return { ...f, supplier_prices: next };
    });
  };
  const setPreferredSupplier = (idx) => {
    setForm((f) => ({
      ...f,
      supplier_prices: f.supplier_prices.map((p, i) => ({ ...p, is_preferred: i === idx })),
    }));
  };
  const applyPackToPrice = (idx) => {
    const sp = form.supplier_prices[idx];
    const calc = computePackUnitPrice(sp.pack_price_chf, sp.pack_size, sp.pack_size_unit);
    if (!calc) {
      toast.error("Renseignez prix, quantité et unité du conditionnement");
      return;
    }
    updateSupplierPrice(idx, {
      price_chf: calc.price.toFixed(4),
      price_unit: calc.unit,
    });
    toast.success(`Prix unitaire calculé : ${calc.price.toFixed(2)} CHF/${calc.unit}`);
  };

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-heading text-[#1A1C1A]">Ingrédients de base</h1>
          <p className="text-sm text-[#575A57]">Référentiel partagé pour les fiches techniques — évite les doublons et les typos.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-ingredient-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouvel ingrédient
        </Button>
      </div>

      <Card className="border-[#E5E5E0]">
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[240px] relative">
              <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-[#A3A6A3]" />
              <Input
                placeholder="Rechercher par nom ou note…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9"
                data-testid="ingredients-search"
              />
            </div>
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-[220px] h-9" data-testid="ingredients-filter-cat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Toutes catégories</SelectItem>
                {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {filtered.length === 0 ? (
            <div className="text-sm text-[#A3A6A3] italic border border-dashed border-[#E5E5E0] rounded p-6 text-center">
              {items.length === 0 ? "Aucun ingrédient. Cliquez sur « Nouvel ingrédient » pour démarrer votre référentiel." : "Aucun ingrédient ne correspond à votre recherche."}
            </div>
          ) : (
            <div className="border border-[#E5E5E0] rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F9F9F6] text-[10px] uppercase tracking-wider text-[#575A57]">
                  <tr>
                    <th className="text-left px-3 py-2 font-semibold">Nom</th>
                    <th className="text-left px-3 py-2 font-semibold">Catégorie</th>
                    <th className="text-left px-3 py-2 font-semibold">Allergènes</th>
                    <th className="text-right px-3 py-2 font-semibold">Prix réf.</th>
                    <th className="text-left px-3 py-2 font-semibold">Fournisseur préf.</th>
                    <th className="px-3 py-2 w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr key={it.id} className="border-t border-[#F0F0EB] hover:bg-[#FAFAF7]" data-testid={`ingredient-row-${it.id}`}>
                      <td className="px-3 py-2 font-medium text-[#1A1C1A]">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span>{it.name}</span>
                          {usageCount[it.id] > 0 && (
                            <Badge
                              variant="outline"
                              className="text-[9px] bg-[#E8F0EA] text-[#1F4E3D] border-[#1F4E3D]/30 font-semibold"
                              title="Nombre de plats utilisant cet ingrédient via la fiche technique"
                              data-testid={`ingredient-usage-${it.id}`}
                            >
                              {usageCount[it.id]} plat{usageCount[it.id] > 1 ? "s" : ""}
                            </Badge>
                          )}
                          {it.yield_percent != null && it.yield_percent < 100 && (
                            <Badge
                              variant="outline"
                              className="text-[9px] bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8] font-mono"
                              title="Rendement après transformation"
                            >
                              {it.yield_percent}%
                            </Badge>
                          )}
                        </div>
                        {it.notes && <div className="text-[11px] text-[#A3A6A3] italic mt-0.5">{it.notes}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px] bg-[#FBF4E9] text-[#8C6B2F] border-[#E8D3A8]">
                          <Tag className="w-2.5 h-2.5 mr-1" />{it.category}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-0.5">
                          {(it.allergens || []).map((a) => (
                            <Badge key={a} variant="outline" className="text-[9px] bg-[#FCEEEA] text-[#C05C47] border-[#E8C1B4]">{a}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-xs">
                        {it.default_price_chf != null ? `${Number(it.default_price_chf).toFixed(2)} CHF/${it.default_price_unit || "kg"}` : "—"}
                        {(it.supplier_prices || []).length > 1 && (
                          <Badge variant="outline" className="ml-1.5 text-[9px] bg-[#FFFCEC] border-[#C1A063]/40 text-[#8C6B2F]" title={`${it.supplier_prices.length} fournisseurs comparés`}>
                            +{it.supplier_prices.length - 1} alt.
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#575A57]">
                        {it.default_supplier_id ? (supplierMap[it.default_supplier_id]?.name || "—") : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1 justify-end">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(it)} className="h-7 w-7" data-testid={`edit-ingredient-${it.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(it)} className="h-7 w-7 text-[#C05C47]" data-testid={`delete-ingredient-${it.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal CRUD */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'ingrédient" : "Nouvel ingrédient"}</DialogTitle>
            <DialogDescription>
              Définissez un ingrédient de base réutilisable dans toutes vos fiches techniques.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label className="mb-1.5 block">Nom *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Riz thaï parfumé"
                  data-testid="form-ing-name"
                  autoFocus
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Catégorie</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="form-ing-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-1.5 block">Unité de référence (recette)</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="form-ing-unit"><SelectValue /></SelectTrigger>
                  <SelectContent>{REF_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Prix multi-fournisseurs */}
            <div className="space-y-2" data-testid="supplier-prices-section">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-[#1A1C1A]">
                  Prix par fournisseur
                  <span className="ml-2 text-[10px] uppercase tracking-wider text-[#A3A6A3] font-normal">
                    Aligro · Prodega · Sun Wah …
                  </span>
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSupplierPrice}
                  className="h-7 text-xs border-[#1F4E3D]/30 text-[#1F4E3D] hover:bg-[#E8EEE9]"
                  data-testid="add-supplier-price-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un fournisseur
                </Button>
              </div>

              {(form.supplier_prices || []).length === 0 ? (
                <p className="text-xs text-[#A3A6A3] italic border border-dashed border-[#E5E5E0] rounded p-3 text-center">
                  Aucun prix saisi. Cliquez sur « Ajouter un fournisseur » pour démarrer.
                  Si vous en saisissez plusieurs (Aligro / Prodega …), le calcul de coût recette
                  utilisera celui marqué <Star className="w-3 h-3 inline -mt-0.5 text-[#C1A063] fill-[#C1A063]" /> préféré.
                </p>
              ) : (
                <div className="space-y-2">
                  {form.supplier_prices.map((sp, idx) => {
                    const calc = computePackUnitPrice(sp.pack_price_chf, sp.pack_size, sp.pack_size_unit);
                    return (
                      <div
                        key={sp.id || idx}
                        className={`border rounded-md p-2.5 space-y-2 ${sp.is_preferred ? "border-[#C1A063] bg-[#FFFCEC]" : "border-[#E5E5E0] bg-white"}`}
                        data-testid={`supplier-price-row-${idx}`}
                      >
                        <div className="flex flex-wrap items-end gap-2">
                          {/* Étoile préféré */}
                          <button
                            type="button"
                            onClick={() => setPreferredSupplier(idx)}
                            className={`h-9 w-9 rounded flex items-center justify-center transition-colors ${sp.is_preferred ? "text-[#C1A063]" : "text-[#D5D7D2] hover:text-[#C1A063]"}`}
                            title={sp.is_preferred ? "Fournisseur préféré (utilisé par défaut dans les fiches techniques)" : "Définir comme préféré"}
                            data-testid={`set-preferred-${idx}`}
                          >
                            <Star className={`w-5 h-5 ${sp.is_preferred ? "fill-[#C1A063]" : ""}`} />
                          </button>

                          {/* Fournisseur */}
                          <div className="flex-1 min-w-[140px]">
                            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">Fournisseur</Label>
                            <Select
                              value={sp.supplier_id || "__none__"}
                              onValueChange={(v) => updateSupplierPrice(idx, { supplier_id: v === "__none__" ? "" : v })}
                            >
                              <SelectTrigger className="h-9" data-testid={`supplier-select-${idx}`}>
                                <SelectValue placeholder="Aucun" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— Aucun —</SelectItem>
                                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Prix */}
                          <div className="w-28">
                            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">Prix (CHF)</Label>
                            <NumericInput
                              value={sp.price_chf}
                              onChange={(e) => updateSupplierPrice(idx, { price_chf: e.target.value })}
                              placeholder="3.50"
                              className="h-9 font-mono text-sm"
                              data-testid={`supplier-price-${idx}`}
                            />
                          </div>

                          {/* Unité */}
                          <div className="w-24">
                            <Label className="text-[10px] uppercase tracking-wider text-[#575A57] mb-1 block">Unité</Label>
                            <Select value={sp.price_unit} onValueChange={(v) => updateSupplierPrice(idx, { price_unit: v })}>
                              <SelectTrigger className="h-9" data-testid={`supplier-price-unit-${idx}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>{PRICE_UNITS.map((u) => <SelectItem key={u} value={u}>/ {u}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>

                          {/* Toggle conditionnement */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => updateSupplierPrice(idx, { _packOpen: !sp._packOpen })}
                            className="h-9 text-xs text-[#8C6B2F] hover:bg-[#FBF4E9]"
                            data-testid={`toggle-pack-${idx}`}
                          >
                            {sp._packOpen ? <ChevronUp className="w-3.5 h-3.5 mr-1" /> : <ChevronDown className="w-3.5 h-3.5 mr-1" />}
                            Calc.
                          </Button>

                          {/* Suppression */}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeSupplierPrice(idx)}
                            className="h-9 w-9 text-[#C05C47]"
                            title="Supprimer cette ligne fournisseur"
                            data-testid={`remove-supplier-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        {/* Calculateur conditionnement (collapsible) */}
                        {sp._packOpen && (
                          <div className="bg-[#FBF4E9] border border-[#E8D3A8] rounded p-2 space-y-1.5">
                            <div className="text-[10px] uppercase tracking-wider text-[#8C6B2F] font-semibold">
                              Calcul depuis conditionnement
                            </div>
                            <div className="grid grid-cols-12 gap-2 items-end">
                              <div className="col-span-4">
                                <Label className="text-[10px] mb-0.5 block">Prix payé</Label>
                                <NumericInput
                                  value={sp.pack_price_chf}
                                  onChange={(e) => updateSupplierPrice(idx, { pack_price_chf: e.target.value })}
                                  placeholder="3.00"
                                  className="h-8 text-sm"
                                  data-testid={`pack-price-${idx}`}
                                />
                              </div>
                              <div className="col-span-1 text-center text-xs text-[#575A57] pb-2">pour</div>
                              <div className="col-span-3">
                                <Label className="text-[10px] mb-0.5 block">Quantité</Label>
                                <NumericInput
                                  value={sp.pack_size}
                                  onChange={(e) => updateSupplierPrice(idx, { pack_size: e.target.value })}
                                  placeholder="250"
                                  className="h-8 text-sm"
                                  data-testid={`pack-size-${idx}`}
                                />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-[10px] mb-0.5 block">Unité</Label>
                                <Select value={sp.pack_size_unit} onValueChange={(v) => updateSupplierPrice(idx, { pack_size_unit: v })}>
                                  <SelectTrigger className="h-8 text-sm" data-testid={`pack-unit-${idx}`}><SelectValue /></SelectTrigger>
                                  <SelectContent>{PRICE_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                                </Select>
                              </div>
                              <div className="col-span-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  disabled={!calc}
                                  onClick={() => applyPackToPrice(idx)}
                                  className="h-8 w-full text-xs border-[#1F4E3D]/30 text-[#1F4E3D] hover:bg-[#E8EEE9]"
                                  data-testid={`apply-pack-${idx}`}
                                >
                                  Appliquer
                                </Button>
                              </div>
                            </div>
                            {calc && (
                              <div className="text-[11px] text-[#1F4E3D] font-mono">
                                = <strong>{calc.price.toFixed(4)} CHF/{calc.unit}</strong>
                                {calc.unit === "kg" && <span className="text-[#A3A6A3]"> · soit {(calc.price/10).toFixed(2)} CHF/100g</span>}
                                {calc.unit === "L" && <span className="text-[#A3A6A3]"> · soit {(calc.price/10).toFixed(2)} CHF/100mL</span>}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Notes ligne */}
                        <Input
                          value={sp.notes}
                          onChange={(e) => updateSupplierPrice(idx, { notes: e.target.value })}
                          placeholder="Note (carton de 5 kg · promo trimestrielle …)"
                          className="h-7 text-xs italic"
                          data-testid={`supplier-notes-${idx}`}
                        />
                      </div>
                    );
                  })}

                  {/* Récapitulatif comparaison si ≥ 2 fournisseurs */}
                  {form.supplier_prices.filter((p) => Number(p.price_chf) > 0).length >= 2 && (() => {
                    // Normalise tous les prix vers CHF/kg pour comparaison
                    const factors = { g: 1000, kg: 1, mg: 1000000, ml: 1000, l: 1, cl: 100, dl: 10, "100g": 10, "100ml": 10, pce: null };
                    const normalized = form.supplier_prices
                      .map((p, idx) => {
                        const f = factors[(p.price_unit || "").toLowerCase()];
                        if (!f || !Number(p.price_chf)) return null;
                        return { idx, supplier_id: p.supplier_id, price_per_kg: Number(p.price_chf) * f, is_preferred: p.is_preferred };
                      })
                      .filter(Boolean);
                    if (normalized.length < 2) return null;
                    const sorted = [...normalized].sort((a, b) => a.price_per_kg - b.price_per_kg);
                    const cheapest = sorted[0];
                    const preferred = normalized.find((n) => n.is_preferred);
                    const savings = preferred && preferred.idx !== cheapest.idx
                      ? ((preferred.price_per_kg - cheapest.price_per_kg) / preferred.price_per_kg * 100).toFixed(1)
                      : null;
                    return (
                      <div className="bg-[#F0F0EB] border border-[#E5E5E0] rounded p-2 text-xs text-[#575A57]" data-testid="supplier-comparison">
                        <span className="font-semibold text-[#1A1C1A]">Comparaison :</span>{" "}
                        {savings ? (
                          <>
                            <Star className="w-3 h-3 inline -mt-0.5 fill-[#C1A063] text-[#C1A063]" />{" "}
                            <strong>{(supplierMap[preferred.supplier_id]?.name) || "Sans fournisseur"}</strong> (préféré) coûte{" "}
                            <strong className="text-[#C05C47]">+{savings}%</strong> vs{" "}
                            <strong className="text-[#1F4E3D]">{(supplierMap[cheapest.supplier_id]?.name) || "Sans fournisseur"}</strong>{" "}
                            ({cheapest.price_per_kg.toFixed(2)} CHF/kg). Envisagez de switcher si la qualité est équivalente.
                          </>
                        ) : (
                          <>
                            <Star className="w-3 h-3 inline -mt-0.5 fill-[#C1A063] text-[#C1A063]" />{" "}
                            Le préféré est aussi le moins cher ({cheapest.price_per_kg.toFixed(2)} CHF/kg). ✓
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div>
              <Label className="mb-2 block">Allergènes contenus</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {ALLERGENS_EU.map((a) => (
                  <label key={a} className="flex items-center gap-2 text-xs cursor-pointer">
                    <Checkbox checked={form.allergens.includes(a)} onCheckedChange={() => toggleAllergen(a)} />
                    {a}
                  </label>
                ))}
              </div>
              <p className="text-xs text-[#575A57] mt-2 italic">
                Sera utile pour détecter automatiquement les allergènes d'un plat à partir de sa recette.
              </p>
            </div>

            <div>
              <Label className="mb-1.5 block flex items-center gap-2">
                Rendement après transformation
                <span className="text-[10px] uppercase tracking-wider text-[#A3A6A3] font-normal">optionnel</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  step="1"
                  min="1"
                  max="100"
                  value={form.yield_percent}
                  onChange={(e) => setForm({ ...form, yield_percent: e.target.value })}
                  placeholder="100"
                  className="w-24 font-mono"
                  data-testid="form-ing-yield"
                />
                <span className="text-sm">%</span>
                <p className="text-xs text-[#575A57] italic flex-1">
                  Ex : poulet entier dont on jette 25 % en parage → saisir 75 %. Le coût réel sera ajusté automatiquement dans les recettes.
                </p>
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block">Notes (optionnel)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Acheter chez Sun Wah · Vérifier le grain long"
                data-testid="form-ing-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
            <Button onClick={submit} disabled={saving || !form.name.trim()} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="form-ing-submit">
              {editing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
